"use client";

import { normalizeProps, useMachine } from "@zag-js/react";
import { useEffect, useId, useRef } from "react";

import * as checkbox from "@zag-js/checkbox";
import { cva } from "class-variance-authority";

import { CheckSmallIcon } from "@ala/icons/custom";

import { clsx } from "../../utils/clsx";

export interface CheckboxProps extends Omit<checkbox.Context, "id"> {
  className?: string;
  children?: React.ReactNode;
  size?: "sm" | "lg";
}

const labelVariants = cva("select-none", {
  variants: {
    size: {
      sm: "text-sm",
      lg: "text-base",
    },
  },
});

export const Checkbox = ({ className, children, size = "sm", onCheckedChange, ...context }: CheckboxProps) => {
  const onCheckedChangeRef = useRef(onCheckedChange);

  const [state, send] = useMachine(checkbox.machine({ id: useId(), ...context }), {
    context: {
      ...context,
      onCheckedChange: (details) => {
        onCheckedChangeRef.current?.(details);
      },
    },
  });
  const labelRef = useRef<HTMLSpanElement>(null);

  const api = checkbox.connect(state, send, normalizeProps);

  useEffect(() => {
    onCheckedChangeRef.current = onCheckedChange;
  }, [onCheckedChange]);

  const labelLineHeight = size === "sm" ? 20 : 24;
  const checkboxOffset = (labelLineHeight - 16) / 2;

  return (
    <label
      {...api.getRootProps()}
      className={clsx(
        "inline-flex flex-row items-start justify-center gap-2.5 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50",
        className,
      )}
    >
      <div
        {...api.getControlProps()}
        aria-label="checkbox"
        className="data-[focus-visible]:outline-auto group relative h-4 w-4 shrink-0 overflow-hidden rounded-xs border border-gray-400 text-xs text-white data-[state=checked]:border-contrast-900 data-[state=checked]:bg-contrast-900"
        style={{
          marginTop: children ? checkboxOffset : 0,
        }}
      >
        <CheckSmallIcon className="absolute top-1/2 left-1/2 -translate-1/2 group-data-[state=unchecked]:hidden" />
      </div>
      {children && (
        <span ref={labelRef} className={labelVariants({ size: size })} {...api.getLabelProps()}>
          {children}
        </span>
      )}
      <input {...api.getHiddenInputProps()} />
    </label>
  );
};

Checkbox.displayName = "Checkbox";
